"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LineChart,
  Line,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ArrowLeft, ChevronDown, Loader2, MessageSquare, Play, RefreshCw, Save, Search, Upload } from "lucide-react";
import { ReviewComment } from "@/components/shared/ReviewComment";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { DocumentManager } from "@/components/planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";

const SECTION_TRR = "Execution_TR_TRR";
const ECL_QOQ_JSON_FILE = "Execution_TR_ECLQ.json";
const TR_AREA_PREFIX = "TR";
const INFERENCE_ID = "ECL-QOQ-TR-INFERENCE";
const INFERENCE_QUESTION = "Document the inference and conclusion from the above procedure performed.";

/** Shown in table/chart — excludes discontinued "Not Due". */
const QOQ_BUCKETS_DISPLAY = ["0-90", "91-180", "181-270", "271-365", ">365"] as const;

const QOQ_BUCKET_LABEL: Record<string, string> = {
  "0-90": "0-90 Days",
  "91-180": "91-180 Days",
  "181-270": "181-270 Days",
  "271-365": "271-365 Days",
  ">365": ">365 Days",
};

const QOQ_CHART_KEYS: Record<string, string> = {
  "0-90": "b0",
  "91-180": "b1",
  "181-270": "b2",
  "271-365": "b3",
  ">365": "b4",
};

const QOQ_LINE_COLORS = ["#4da3ff", "#a78bfa", "#34d399", "#fbbf24", "#fb7185"];

type EclQoqBucketBlock = { amount?: number; percentage?: number | null };
type EclQoqJson = Record<string, Record<string, EclQoqBucketBlock> | undefined>;

const QOQ_BUCKET_ALIASES: Record<string, string[]> = {
  "0-90": ["0-90 Days", "0-90 days"],
  "91-180": ["91-180 Days", "91-180 days"],
  "181-270": ["181-270 Days", "181-270 days"],
  "271-365": ["271-365 Days", "271-365 days"],
};

function parseDdMmYyyy(s: string): Date {
  const parts = s.split("/");
  if (parts.length !== 3) return new Date(0);
  const d = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const y = parseInt(parts[2], 10);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return new Date(0);
  return new Date(y, m, d);
}

function formatHeaderDdMmYy(s: string): string {
  const parts = s.split("/");
  if (parts.length !== 3) return s;
  const [dd, mm, yyyy] = parts;
  const yy = yyyy.slice(-2);
  return `${dd.padStart(2, "0")}-${mm.padStart(2, "0")}-${yy}`;
}

function sortEclDateKeys(data: EclQoqJson): string[] {
  return Object.keys(data)
    .filter((k) => k !== "Analysis" && k !== "threshold")
    .sort((a, b) => parseDdMmYyyy(a).getTime() - parseDdMmYyyy(b).getTime());
}

function formatAmount(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function getBucketKeyCandidates(bucket: string): string[] {
  return [bucket, ...(QOQ_BUCKET_ALIASES[bucket] || [])];
}

function getBucketBlock(data: EclQoqJson, dateKey: string, bucket: string): EclQoqBucketBlock | null {
  const dateBlock = data[dateKey];
  if (!dateBlock || typeof dateBlock !== "object") return null;
  for (const key of getBucketKeyCandidates(bucket)) {
    const block = dateBlock[key];
    if (block && typeof block === "object") return block;
  }
  return null;
}

function getBucketAmount(data: EclQoqJson, dateKey: string, bucket: string): number | null {
  const block = getBucketBlock(data, dateKey, bucket);
  if (block == null || typeof block.amount !== "number") return null;
  return block.amount;
}

function formatPercentageCell(
  data: EclQoqJson,
  _leftDate: string,
  rightDate: string,
  bucket: string,
): string {
  const p = getBucketBlock(data, rightDate, bucket)?.percentage;
  if (p === null || p === undefined) return "NA";
  return `${(Number(p) * 100).toFixed(0)}%`;
}

function buildQoqChartRows(data: EclQoqJson, sortedDates: string[]) {
  const rows: Record<string, string | number | null>[] = [];
  for (let i = 0; i < sortedDates.length - 1; i++) {
    const left = sortedDates[i];
    const right = sortedDates[i + 1];
    const period = `${formatHeaderDdMmYy(left)} → ${formatHeaderDdMmYy(right)}`;
    const row: Record<string, string | number | null> = { period };
    for (const bucket of QOQ_BUCKETS_DISPLAY) {
      const key = QOQ_CHART_KEYS[bucket];
      const p = getBucketBlock(data, right, bucket)?.percentage;
      row[key] = p != null && !Number.isNaN(Number(p)) ? Number(p) * 100 : null;
    }
    rows.push(row);
  }
  return rows;
}

type AnalysisRow = {
  bucket: string;
  label: string;
  average: number | null;
  closingBalance: number | null;
  creditRiskExposure: number | null;
};

function computeQoqAnalysisRows(data: EclQoqJson, sortedDates: string[]): AnalysisRow[] {
  if (!sortedDates.length) return [];
  const latestDate = sortedDates[sortedDates.length - 1];
  return QOQ_BUCKETS_DISPLAY.map((bucket) => {
    const pValues: number[] = [];
    for (const d of sortedDates) {
      const p = getBucketBlock(data, d, bucket)?.percentage;
      if (p != null && !Number.isNaN(Number(p))) pValues.push(Number(p));
    }
    const average = pValues.length ? pValues.reduce((a, b) => a + b, 0) / pValues.length : null;
    const closingBalance = getBucketAmount(data, latestDate, bucket);
    const creditRiskExposure =
      average != null && closingBalance != null ? average * closingBalance : null;
    return {
      bucket,
      label: QOQ_BUCKET_LABEL[bucket],
      average,
      closingBalance,
      creditRiskExposure,
    };
  });
}

interface ExcelColumn {
  name: string;
  value: string;
}

function getAnalysisProvisionByBucket(
  analysisObj: Record<string, any>,
  bucket: string,
): unknown {
  for (const key of getBucketKeyCandidates(bucket)) {
    const v = analysisObj?.[key]?.["Provision Recognized"];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

interface TrdRcvEclFlowRateQoqProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

function toReportingDateStr(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

export default function TrdRcvEclFlowRateQoq({ onBack, setActiveSection }: TrdRcvEclFlowRateQoqProps) {
  const handleBack =
    onBack ||
    (() => {
      setActiveSection?.("trdrcv-substantive");
    });

  const getSectionQuestionId = (sectionName: string): string => {
    const normalized = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `TRDRCV_ECL_QOQ_${normalized}`;
  };

  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [dateColumn, setDateColumn] = useState<string>("");
  const [amountColumn, setAmountColumn] = useState<string>("");
  const [reportingDateIso, setReportingDateIso] = useState<string>("");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">(
    "idle",
  );
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [runStatus, setRunStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [runMessage, setRunMessage] = useState<string>("");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [isInputSectionOpen, setIsInputSectionOpen] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  const [eclQoqData, setEclQoqData] = useState<EclQoqJson | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  // Threshold inputs (user-controlled)
  const [performanceMateriality, setPerformanceMateriality] = useState<string>("1000000");
  const [riskAssessment, setRiskAssessment] = useState<"Lower" | "Higher">("Higher");
  const [controlReliance, setControlReliance] = useState<
    "Relying on Control" | "Not Relying on Control"
  >("Relying on Control");
  const [isThresholdInputsOpen, setIsThresholdInputsOpen] = useState(false);

  const [thresholdRunStatus, setThresholdRunStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [thresholdRunMessage, setThresholdRunMessage] = useState<string>("");
  const [analysisProvisionInput, setAnalysisProvisionInput] = useState<Record<string, string>>({});
  const [analysisSaveStatus, setAnalysisSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [analysisSaveMessage, setAnalysisSaveMessage] = useState<string>("");

  const loadEclQResults = useCallback(async () => {
    setResultsLoading(true);
    setResultsError(null);
    try {
      if (!window.sharePointAPI?.downloadClientJson) {
        setEclQoqData(null);
        setResultsError("SharePoint download is not available.");
        return;
      }
      const result = await window.sharePointAPI.downloadClientJson(ECL_QOQ_JSON_FILE);
      if (!result.success) {
        setEclQoqData(null);
        setResultsError(result.error || "Failed to download JSON");
        return;
      }
      const raw = result.data;
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        const parsed = raw as EclQoqJson;
        setEclQoqData(parsed);
        const analysisObj = (parsed as any)?.Analysis || {};
        const provisionMap: Record<string, string> = {};
        for (const bucket of QOQ_BUCKETS_DISPLAY) {
          const v = getAnalysisProvisionByBucket(analysisObj, bucket);
          provisionMap[bucket] =
            typeof v === "number" && !Number.isNaN(v) ? String(v) : "";
        }
        setAnalysisProvisionInput(provisionMap);
      } else {
        setEclQoqData({});
        setAnalysisProvisionInput({});
      }
    } catch (e) {
      setEclQoqData(null);
      setResultsError((e as Error).message);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEclQResults();
  }, [loadEclQResults]);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };

    const loadInferenceQuestion = async () => {
      try {
        const fileName = `Execution_${TR_AREA_PREFIX}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceData = result.data.substantiveProcedures[INFERENCE_ID];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: inferenceData.documents || [],
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };

    loadCompany();
    loadInferenceQuestion();
  }, []);

  const saveInferenceQuestion = async () => {
    try {
      setInferenceSaveStatus("saving");
      setInferenceSaveError(null);

      if (!companyName) {
        throw new Error("Company name not available");
      }

      const fileName = `Execution_${TR_AREA_PREFIX}_Responses.json`;
      let existingData: any = { substantiveProcedures: {} };

      const existingResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
      if (existingResult?.success && existingResult?.data) {
        existingData = existingResult.data;
        if (!existingData.substantiveProcedures) {
          existingData.substantiveProcedures = {};
        }
      }

      let inferenceDocumentNames: string[] = [];
      const sectionToFind = `Execution_${TR_AREA_PREFIX}_${INFERENCE_ID}`;
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${INFERENCE_ID}`);
      const hasInferenceTextboxContent =
        inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;

      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[INFERENCE_ID] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${TR_AREA_PREFIX} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${TR_AREA_PREFIX}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus("error");
    }
  };

  const sortedDates = useMemo(
    () => (eclQoqData ? sortEclDateKeys(eclQoqData) : []),
    [eclQoqData],
  );

  const chartRows = useMemo(
    () => (eclQoqData && sortedDates.length >= 2 ? buildQoqChartRows(eclQoqData, sortedDates) : []),
    [eclQoqData, sortedDates],
  );
  const analysisRows = useMemo(
    () => (eclQoqData ? computeQoqAnalysisRows(eclQoqData, sortedDates) : []),
    [eclQoqData, sortedDates],
  );

  const eclThreshold = (eclQoqData as any)?.threshold as
    | { population?: number; amount?: number }
    | undefined;
  const thresholdAmount = typeof eclThreshold?.amount === "number" ? eclThreshold.amount : null;

  const { totalCreditRiskExposure, totalProvisionRecognized, absDifferenceSum } = useMemo(() => {
    let exposureTotal = 0;
    let provisionTotal = 0;

    for (const row of analysisRows) {
      const provisionRaw = analysisProvisionInput[row.bucket];
      const provisionCandidate =
        provisionRaw != null && provisionRaw.trim() !== "" ? Number(provisionRaw) : 0;
      const safeProvision = Number.isFinite(provisionCandidate) ? provisionCandidate : 0;

      exposureTotal += row.creditRiskExposure ?? 0;
      provisionTotal += safeProvision;
    }

    return {
      totalCreditRiskExposure: exposureTotal,
      totalProvisionRecognized: provisionTotal,
      absDifferenceSum: Math.abs(exposureTotal - provisionTotal),
    };
  }, [analysisRows, analysisProvisionInput]);
  const isWithinThreshold =
    thresholdAmount != null && Number.isFinite(thresholdAmount) ? absDifferenceSum <= thresholdAmount : null;

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles(SECTION_TRR);
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: { name?: string }) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    searchForFiles();
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          setUploadStatus("idle");
          return;
        }
        try {
          const base64Content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(",")[1]);
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
          });

          const fyYear = getFY_YEAR();
          if (!fyYear) {
            throw new Error("FY_YEAR is not configured. Please select an engagement.");
          }

          if (window.sharePointAPI?.uploadFile) {
            const result = await window.sharePointAPI.uploadFile({
              fileContent: base64Content,
              fileName: file.name,
              referenceValue: referenceValue,
              folderName: "client",
              fyYear: fyYear,
              sectionList: [SECTION_TRR],
            });

            if (result.success) {
              setSelectedFile(file.name);
              setFoundFiles([file.name]);
              setFileSearchStatus("found");
              setUploadStatus("completed");
            } else {
              setUploadStatus("error");
            }
          } else {
            setUploadStatus("error");
          }
        } catch {
          setUploadStatus("error");
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
        }
      }
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const canRun =
    selectedFile &&
    dateColumn &&
    amountColumn &&
    reportingDateIso &&
    window.payroll?.runTrdrcvEclFlowQoq;

  const runAnalysis = async () => {
    if (!canRun || !window.payroll?.runTrdrcvEclFlowQoq) return;

    const reportingDateStr = toReportingDateStr(reportingDateIso);
    if (!reportingDateStr) {
      setRunStatus("error");
      setRunMessage("Invalid reporting date");
      return;
    }

    setRunStatus("running");
    setRunMessage("");
    try {
      const result = await window.payroll.runTrdrcvEclFlowQoq({
        fileName: selectedFile,
        dateColumn,
        amountColumn,
        reportingDateStr,
      });
      if (result.ok) {
        setRunStatus("completed");
        setRunMessage(result.message || "Completed successfully");
        await loadEclQResults();
      } else {
        setRunStatus("error");
        setRunMessage(result.error || "Run failed");
      }
    } catch (e) {
      setRunStatus("error");
      setRunMessage((e as Error).message);
    }
  };

  const canRunThreshold =
    Number.isFinite(Number(performanceMateriality)) &&
    Number(performanceMateriality) > 0 &&
    !!window.payroll?.runTrdrcvEclQoqThreshold &&
    thresholdRunStatus !== "running";

  const runThreshold = async () => {
    if (!canRunThreshold || !window.payroll?.runTrdrcvEclQoqThreshold) return;

    const pm = Number(performanceMateriality);
    if (!pm || pm <= 0) {
      setThresholdRunStatus("error");
      setThresholdRunMessage("Performance Materiality must be > 0");
      return;
    }

    setThresholdRunStatus("running");
    setThresholdRunMessage("");

    try {
      const result = await window.payroll.runTrdrcvEclQoqThreshold({
        performanceMateriality: pm,
        riskAssessment,
        controlReliance,
      });

      if (result.ok) {
        setThresholdRunStatus("completed");
        setThresholdRunMessage(result.message || "Threshold updated");
        await loadEclQResults();
      } else {
        setThresholdRunStatus("error");
        setThresholdRunMessage(result.error || "Threshold run failed");
      }
    } catch (e) {
      setThresholdRunStatus("error");
      setThresholdRunMessage((e as Error).message);
    }
  };

  const saveAnalysis = async () => {
    const spApi = (window as any).sharePointAPI;
    if (!eclQoqData || !spApi?.uploadClientJson) return;

    const updated: any = { ...(eclQoqData as any) };
    if (!updated.Analysis || typeof updated.Analysis !== "object") {
      updated.Analysis = {};
    }

    for (const row of analysisRows) {
      const bucket = row.bucket;
      const provisionRaw = analysisProvisionInput[bucket];
      const provisionCandidate =
        provisionRaw != null && provisionRaw.trim() !== "" ? Number(provisionRaw) : 0;
      const safeProvision = Number.isFinite(provisionCandidate) ? provisionCandidate : 0;
      const difference = (row.creditRiskExposure ?? 0) - safeProvision;

      updated.Analysis[bucket] = {
        ...(updated.Analysis[bucket] || {}),
        Average: row.average,
        "Closing Balance": row.closingBalance,
        "Credit Risk Exposure": row.creditRiskExposure,
        "Provision Recognized": safeProvision,
        Difference: difference,
      };
    }

    setAnalysisSaveStatus("saving");
    setAnalysisSaveMessage("");
    try {
      const res = await spApi.uploadClientJson(ECL_QOQ_JSON_FILE, updated);
      if (res?.success) {
        setAnalysisSaveStatus("saved");
        setAnalysisSaveMessage("Analysis saved");
        await loadEclQResults();
      } else {
        setAnalysisSaveStatus("error");
        setAnalysisSaveMessage(res?.error || "Failed to save analysis");
      }
    } catch (e) {
      setAnalysisSaveStatus("error");
      setAnalysisSaveMessage((e as Error).message);
    }
  };

  return (
    <div className="flex h-full flex-col space-y-6 overflow-auto p-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">ECL Flow Rate Analysis – QoQ</h2>
        </div>
        <Button variant="outline" onClick={handleBack} className="border-white/20 text-white">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader
          className="pb-4 cursor-pointer select-none"
          onClick={() => setIsInputSectionOpen((prev) => !prev)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-white">Inputs</CardTitle>
              <p className="mt-1 text-sm text-white/50">
                Expand to complete Step 1, Step 2, and run the procedure (section {SECTION_TRR}).
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setIsInputSectionOpen((prev) => !prev);
              }}
              className="shrink-0 text-white hover:bg-white/10"
              aria-label={isInputSectionOpen ? "Collapse input section" : "Expand input section"}
            >
              <ChevronDown
                className={`h-5 w-5 transition-transform ${isInputSectionOpen ? "rotate-180" : "rotate-0"}`}
              />
            </Button>
          </div>
        </CardHeader>
        {isInputSectionOpen && <CardContent className="space-y-6">
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-white">
                <Search className="h-4 w-4 shrink-0 text-blue-500" />
                Step 1: File from client ({SECTION_TRR})
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1"))}
                className="h-7 w-7 shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-3">
              {fileSearchStatus === "searching" && (
                <span className="text-white/60">Searching…</span>
              )}
              {fileSearchStatus === "found" && <span className="font-bold text-green-400">FOUND</span>}
              {fileSearchStatus === "not_found" && (
                <span className="text-yellow-400">No files found for this section</span>
              )}
            </div>

            {fileSearchStatus === "found" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Select file</Label>
                  <Select value={selectedFile} onValueChange={setSelectedFile}>
                    <SelectTrigger className="border-white/10 bg-black/40 text-white">
                      <SelectValue placeholder="Select file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedFile && (
                  <Button
                    onClick={loadExcelColumns}
                    disabled={!selectedFile || isLoadingExcelColumns}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {isLoadingExcelColumns ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading columns…
                      </>
                    ) : (
                      "Load columns"
                    )}
                  </Button>
                )}
              </div>
            )}

            {fileSearchStatus === "not_found" && (
              <div className="space-y-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4">
                <div className="font-medium text-yellow-400">Upload a Trade Receivable Register to client.</div>
                <div className="space-y-2">
                  <Label className="text-white">Reference (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    className="border-white/10 bg-black/40 text-white"
                    placeholder="Reference"
                  />
                </div>
                <Button
                  onClick={uploadFile}
                  disabled={uploadStatus === "uploading"}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  {uploadStatus === "uploading" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" /> Upload file
                    </>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">Uploaded. Load columns after selecting the file above.</span>
                )}
                {uploadStatus === "error" && <span className="text-red-400">Upload failed</span>}
              </div>
            )}
          </section>

          <section className="space-y-4 border-t border-white/10 pt-6">
            <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-3">
              <h3 className="text-base font-semibold text-white">Step 2: Columns & reporting date</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2"))}
                className="h-7 w-7 shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white">Date</Label>
                <Select
                  value={dateColumn}
                  onValueChange={setDateColumn}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger className="border-white/10 bg-black/40 text-white">
                    <SelectValue placeholder={excelColumns.length ? "Column for dates" : "Load columns first"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Amount</Label>
                <Select
                  value={amountColumn}
                  onValueChange={setAmountColumn}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger className="border-white/10 bg-black/40 text-white">
                    <SelectValue placeholder={excelColumns.length ? "Column for amounts" : "Load columns first"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Reporting date</Label>
              <Input
                type="date"
                value={reportingDateIso}
                onChange={(e) => setReportingDateIso(e.target.value)}
                className="max-w-xs border-white/10 bg-black/40 text-white"
              />
            </div>
          </section>

          <section className="space-y-4 border-t border-white/10 pt-6">
            <div className="flex items-center gap-2 border-b border-white/10 pb-3">
              <Play className="h-4 w-4 shrink-0 text-green-500" />
              <h3 className="text-base font-semibold text-white">Run</h3>
            </div>
            <div className="space-y-3">
              <Button
                onClick={runAnalysis}
                disabled={!canRun || runStatus === "running"}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {runStatus === "running" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" /> Run ECL QoQ
                  </>
                )}
              </Button>
              {runStatus === "completed" && <p className="text-sm text-green-400">{runMessage}</p>}
              {runStatus === "error" && <p className="text-sm text-red-400">{runMessage}</p>}
              {!window.payroll?.runTrdrcvEclFlowQoq && (
                <p className="text-sm text-yellow-400">Desktop API not available (run from Electron app).</p>
              )}
            </div>
          </section>
        </CardContent>}
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-white">Flow rate table & trend</CardTitle>
            <p className="mt-1 text-xs text-white/50">
              From {ECL_QOQ_JSON_FILE} (juggernaut). Percentages apply to the right-hand date in each pair;
              &quot;Not Due&quot; is omitted from particulars.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={resultsLoading}
            onClick={() => void loadEclQResults()}
            className="shrink-0 border-white/20 text-white"
          >
            {resultsLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {resultsError && <p className="text-sm text-amber-400">{resultsError}</p>}
          {sortedDates.length === 0 && !resultsLoading && !resultsError && (
            <p className="text-sm text-white/60">
              No reporting dates in file yet. Run the procedure above to populate {ECL_QOQ_JSON_FILE}.
            </p>
          )}

          {sortedDates.length > 0 && eclQoqData && (
            <div className="rounded-md border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="sticky left-0 z-10 min-w-[100px] bg-black/95 text-white">
                      Particulars
                    </TableHead>
                    {sortedDates.length >= 2
                      ? sortedDates.slice(0, -1).map((_, groupIdx) => {
                          const dLeft = sortedDates[groupIdx];
                          const dRight = sortedDates[groupIdx + 1];
                          return (
                            <React.Fragment key={`${dLeft}-${dRight}-hdr`}>
                              <TableHead className="whitespace-nowrap text-center text-xs text-white">
                                {formatHeaderDdMmYy(dLeft)}
                              </TableHead>
                              <TableHead className="whitespace-nowrap text-center text-xs text-white">
                                {formatHeaderDdMmYy(dRight)}
                              </TableHead>
                              <TableHead className="whitespace-nowrap text-center text-xs text-white">
                                Percentage
                              </TableHead>
                            </React.Fragment>
                          );
                        })
                      : sortedDates.map((d) => (
                          <TableHead key={d} className="whitespace-nowrap text-center text-xs text-white">
                            {formatHeaderDdMmYy(d)}
                          </TableHead>
                        ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {QOQ_BUCKETS_DISPLAY.map((bucket) => (
                    <TableRow key={bucket} className="border-white/10">
                      <TableCell className="sticky left-0 z-10 bg-black/90 font-medium text-white">
                        {QOQ_BUCKET_LABEL[bucket]}
                      </TableCell>
                      {sortedDates.length >= 2
                        ? sortedDates.slice(0, -1).map((_, groupIdx) => {
                            const dLeft = sortedDates[groupIdx];
                            const dRight = sortedDates[groupIdx + 1];
                            return (
                              <React.Fragment key={`${bucket}-${dLeft}-${dRight}`}>
                                <TableCell className="text-right text-xs text-white/90 tabular-nums">
                                  {formatAmount(getBucketAmount(eclQoqData, dLeft, bucket))}
                                </TableCell>
                                <TableCell className="text-right text-xs text-white/90 tabular-nums">
                                  {formatAmount(getBucketAmount(eclQoqData, dRight, bucket))}
                                </TableCell>
                                <TableCell className="min-w-[72px] text-right text-xs text-white/90">
                                  {formatPercentageCell(eclQoqData, dLeft, dRight, bucket)}
                                </TableCell>
                              </React.Fragment>
                            );
                          })
                        : sortedDates.map((d) => (
                            <TableCell
                              key={`${bucket}-${d}`}
                              className="text-right text-xs text-white/90 tabular-nums"
                            >
                              {formatAmount(getBucketAmount(eclQoqData, d, bucket))}
                            </TableCell>
                          ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {chartRows.length > 0 && (
            <div>
              <p className="mb-3 text-sm text-white/70">
                Percentage columns over time (one series per ageing bracket)
              </p>
              <div className="h-[340px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartRows}
                    margin={{ top: 8, right: 20, left: 4, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                    <XAxis
                      dataKey="period"
                      tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 11 }}
                      height={56}
                    />
                    <YAxis
                      tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 11 }}
                      tickFormatter={(v) => `${v}%`}
                      width={44}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(0,0,0,0.92)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 8,
                      }}
                      labelStyle={{ color: "#fff" }}
                      formatter={(value: number | string) =>
                        value != null && typeof value === "number" && !Number.isNaN(value)
                          ? `${value.toFixed(1)}%`
                          : "NA"
                      }
                    />
                    <Legend wrapperStyle={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }} />
                    {QOQ_BUCKETS_DISPLAY.map((bucket, i) => (
                      <Line
                        key={bucket}
                        type="monotone"
                        dataKey={QOQ_CHART_KEYS[bucket]}
                        name={QOQ_BUCKET_LABEL[bucket]}
                        stroke={QOQ_LINE_COLORS[i % QOQ_LINE_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {analysisRows.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-white/70">
                Analysis table (row-wise averages, latest closing balance, exposure, provision and difference)
              </p>
              <div className="rounded-md border border-white/10">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white">Ageing Bracket</TableHead>
                      <TableHead className="text-right text-white">Average</TableHead>
                      <TableHead className="text-right text-white">Closing Balance</TableHead>
                      <TableHead className="text-right text-white">Credit Risk Exposure</TableHead>
                      <TableHead className="text-right text-white">Provision Recognized</TableHead>
                      <TableHead className="text-right text-white">Difference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysisRows.map((row) => {
                      const provisionRaw = analysisProvisionInput[row.bucket];
      const provisionCandidate =
        provisionRaw != null && provisionRaw.trim() !== "" ? Number(provisionRaw) : 0;
      const safeProvision = Number.isFinite(provisionCandidate) ? provisionCandidate : 0;
                      const difference = (row.creditRiskExposure ?? 0) - safeProvision;

                      return (
                        <TableRow key={`analysis-${row.bucket}`} className="border-white/10">
                          <TableCell className="text-white">{row.label}</TableCell>
                          <TableCell className="text-right text-white/90">
                            {row.average != null ? `${(row.average * 100).toFixed(2)}%` : "NA"}
                          </TableCell>
                          <TableCell className="text-right text-white/90">
                            {formatAmount(row.closingBalance)}
                          </TableCell>
                          <TableCell className="text-right text-white/90">
                            {formatAmount(row.creditRiskExposure)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              inputMode="numeric"
                              value={analysisProvisionInput[row.bucket] ?? ""}
                              onChange={(e) =>
                                setAnalysisProvisionInput((prev) => ({
                                  ...prev,
                                  [row.bucket]: e.target.value,
                                }))
                              }
                              className="ml-auto h-8 w-[150px] border-white/10 bg-black/40 text-right text-white"
                            />
                          </TableCell>
                          <TableCell className="text-right text-white/90">
                            {formatAmount(difference)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={saveAnalysis}
                  disabled={analysisSaveStatus === "saving"}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {analysisSaveStatus === "saving" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                {analysisSaveStatus === "saved" && (
                  <span className="text-sm text-green-400">{analysisSaveMessage}</span>
                )}
                {analysisSaveStatus === "error" && (
                  <span className="text-sm text-red-400">{analysisSaveMessage}</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-white">Threshold analysis</CardTitle>
            <p className="mt-1 text-xs text-white/50">
              Compute threshold from {ECL_QOQ_JSON_FILE} (population = sum of latest bucket amounts).
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2"
              onClick={() => setIsThresholdInputsOpen((v) => !v)}
            >
              <span className="text-sm font-semibold text-white">Threshold Inputs</span>
              <ChevronDown
                className={`h-4 w-4 text-white/70 transition-transform ${isThresholdInputsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isThresholdInputsOpen && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Performance Materiality</Label>
                  <Input
                    inputMode="numeric"
                    type="number"
                    value={performanceMateriality}
                    onChange={(e) => setPerformanceMateriality(e.target.value)}
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Risk Assessment</Label>
                  <Select
                    value={riskAssessment}
                    onValueChange={(v) => setRiskAssessment(v as "Lower" | "Higher")}
                  >
                    <SelectTrigger className="border-white/10 bg-black/40 text-white">
                      <SelectValue placeholder="Select risk" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      <SelectItem value="Lower">Lower</SelectItem>
                      <SelectItem value="Higher">Higher</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Control Reliance</Label>
                  <Select
                    value={controlReliance}
                    onValueChange={(v) =>
                      setControlReliance(v as "Relying on Control" | "Not Relying on Control")
                    }
                  >
                    <SelectTrigger className="border-white/10 bg-black/40 text-white">
                      <SelectValue placeholder="Select control reliance" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      <SelectItem value="Relying on Control">Relying on Control</SelectItem>
                      <SelectItem value="Not Relying on Control">Not Relying on Control</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={runThreshold}
              disabled={!canRunThreshold}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {thresholdRunStatus === "running" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" /> Run Threshold
                </>
              )}
            </Button>

            {thresholdRunStatus === "completed" && (
              <span className="text-green-400 text-sm">{thresholdRunMessage}</span>
            )}
            {thresholdRunStatus === "error" && (
              <span className="text-red-400 text-sm">{thresholdRunMessage}</span>
            )}
          </div>

          {thresholdAmount != null && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-xs uppercase tracking-wide text-white/55">Threshold Amount</p>
                  <p className="mt-1 text-lg font-semibold text-white">
                    {thresholdAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-xs uppercase tracking-wide text-white/55">Deviation from Expectation</p>
                  <p className="mt-1 text-lg font-semibold text-white">
                    {absDifferenceSum.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </p>
                  {isWithinThreshold != null && (
                    <p
                      className={`mt-1 text-sm font-medium ${isWithinThreshold ? "text-green-400" : "text-red-400"}`}
                    >
                      {isWithinThreshold ? "Within threshold" : "Exceeds threshold"}
                    </p>
                  )}
                </div>
              </div>

              <div className="h-[280px] rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        cluster: "Credit Risk Exposure vs Provision Recognized",
                        creditExposure: totalCreditRiskExposure,
                        provisionRecognized: totalProvisionRecognized,
                        deviation: null,
                        threshold: null,
                      },
                      {
                        cluster: "Deviation from Expectation vs Threshold Amount",
                        creditExposure: null,
                        provisionRecognized: null,
                        deviation: absDifferenceSum,
                        threshold: thresholdAmount,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="cluster" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                    <YAxis
                      tick={{ fill: "#cbd5e1", fontSize: 11 }}
                      tickFormatter={(v) =>
                        Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })
                      }
                      width={60}
                    />
                    <Tooltip
                      formatter={(value: any) => {
                        if (typeof value === "number" && Number.isFinite(value)) {
                          return value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
                        }
                        return "—";
                      }}
                    />
                    <Legend />
                    <Bar dataKey="creditExposure" fill="#38bdf8" name="Total Credit Risk Exposure" />
                    <Bar
                      dataKey="provisionRecognized"
                      fill="#fbbf24"
                      name="Total Provision Recognized"
                    />
                    <Bar dataKey="deviation" fill="#fb7185" name="Deviation from Expectation" />
                    <Bar dataKey="threshold" fill="#a78bfa" name="Threshold Amount" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">{INFERENCE_QUESTION}</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
              {inferenceSaveStatus === "error" && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === "saving" || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === "saving" ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="execution-substantive:Trade Receivables"
            itemId={INFERENCE_ID}
            itemQuestion={INFERENCE_QUESTION}
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={reviewCommentQuestionId.replace(/^TRDRCV_ECL_QOQ_/, "").replace(/_/g, " ")}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
